import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import type DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import defaultShiftsCollection from 'modules/shift_management/store/api/collections/defaultShifts'
import { useBulkCudPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCudPlannedBreaks'

import buildShiftsVisibility from 'modules/shift_management/lib/buildShiftsVisibility'
import type ShiftForm from 'modules/shift_management/lib/forms/ShiftForm'
import { trackAddDefaultShiftSubmit } from 'modules/shift_management/lib/trackEvents'

export interface Props {
  customShiftForm: ShiftForm
  defaultShiftForm: DefaultShiftForm
  plannedBreakForms?: PlannedBreakForm[]
  onSuccess?: (defaultShiftId: string) => void
}

const useSaveShift = ({
  customShiftForm,
  defaultShiftForm,
  plannedBreakForms,
  onSuccess,
}: Props) => {
  const { bulkCud } = useBulkCudPlannedBreaks()

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async () => {
      const result = await asyncAction(
        () => {
          const { teamsIdsVisibilities, locationsIdsVisibilities } = buildShiftsVisibility({
            form: defaultShiftForm,
            isSingleSelector: false,
          })

          trackAddDefaultShiftSubmit({
            startAt: customShiftForm.get('start_time').value,
            endAt: customShiftForm.get('end_time').value,
            workplace: customShiftForm.get('location_id').value,
            workarea: customShiftForm.get('work_area_id').value,
            comments: customShiftForm.get('notes').value,
            // From save shift toggle
            name: defaultShiftForm.get('title').value,
            color: nameFromHexCode(defaultShiftForm.get('color').value),
            allCompany: defaultShiftForm.get('all_company').value,
            teamsIdsVisibilities,
            locationsIdsVisibilities,
            from: 'save_shift_toggle',
          })

          // TODO: Move to graphql
          return defaultShiftsCollection.create({
            // From custom shift form
            start_at: customShiftForm.get('start_time').value,
            end_at: customShiftForm.get('end_time').value,
            location_id: customShiftForm.get('location_id').value,
            work_area_id: customShiftForm.get('work_area_id').value,
            notes: customShiftForm.get('notes').value,
            // From save shift toggle
            title: defaultShiftForm.get('title').value,
            color: defaultShiftForm.get('color').value ? defaultShiftForm.get('color').value : null,
            all_company: !!defaultShiftForm.get('all_company').value,
            teams_ids_visibilities: teamsIdsVisibilities,
            locations_ids_visibilities: locationsIdsVisibilities,
            extra_hours: !!defaultShiftForm.get('extra_hours').value,
          })
        },
        {
          successFlash: false,
        }
      )

      if (isSuccessful(result)) {
        const defaultShiftId = result.value.id.toString()

        if (plannedBreakForms) {
          await asyncAction(() =>
            bulkCud({
              plannedBreaksParams: [
                {
                  defaultShiftId,
                  plannedBreaks: plannedBreakForms.map((pb) => ({
                    startAt: pb.get('start_at').value,
                    endAt: pb.get('end_at').value,
                    duration: +pb.get('duration').value,
                    breakType: pb.get('break_type').value,
                    breakConfigurationId: pb.get('break_configuration.id').value,
                  })),
                },
              ],
            })
          )
        }

        if (onSuccess) onSuccess(defaultShiftId)
      }
    },
    {
      successMessage: i18n.t('shift_management.saved_shifts.actions.create.success'),
      errorFlash: false,
    }
  )

  return {
    submitting,
    onSubmit,
  }
}

export { useSaveShift }
